import { useMemo, useState } from 'react';

export interface PricePoint {
  date: number; // ms
  price: number;
  storeKey: string;
  storeLabel: string;
}

// Farbenblind-taugliche Palette, in Hell- und Dunkelmodus gut lesbar.
const COLORS = ['#2a78d6', '#e8710a', '#1d8f5a', '#a43fb5', '#d23f5a', '#8a6d00', '#00929f', '#6a6f7a'];

const W = 600;
const H = 260;
const PAD = { top: 12, right: 16, bottom: 28, left: 46 };

const fmtDate = (t: number) => new Date(t).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
const fmtPrice = (v: number) => `${v.toFixed(2).replace('.', ',')} €`;

export function PriceHistoryChart({ points }: { points: PricePoint[] }) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hover, setHover] = useState<PricePoint | null>(null);

  const stores = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of points) if (!m.has(p.storeKey)) m.set(p.storeKey, p.storeLabel);
    return [...m.entries()].map(([key, label], i) => ({ key, label, color: COLORS[i % COLORS.length] }));
  }, [points]);

  const visible = points.filter((p) => !hidden.has(p.storeKey));
  const all = visible.length ? visible : points;
  let minT = Math.min(...all.map((p) => p.date));
  let maxT = Math.max(...all.map((p) => p.date));
  if (minT === maxT) { minT -= 86400000; maxT += 86400000; }
  const minP0 = Math.min(...all.map((p) => p.price));
  const maxP0 = Math.max(...all.map((p) => p.price));
  const span = maxP0 - minP0 || Math.max(maxP0 * 0.2, 0.5);
  const minP = Math.max(0, minP0 - span * 0.15);
  const maxP = maxP0 + span * 0.15;

  const x = (t: number) => PAD.left + ((t - minT) / (maxT - minT)) * (W - PAD.left - PAD.right);
  const y = (v: number) => H - PAD.bottom - ((v - minP) / (maxP - minP)) * (H - PAD.top - PAD.bottom);
  const yTicks = Array.from({ length: 5 }, (_, i) => minP + ((maxP - minP) * i) / 4);
  const xTicks = Array.from({ length: 4 }, (_, i) => minT + ((maxT - minT) * i) / 3);

  function toggle(key: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  return (
    <div className="stack" style={{ gap: '0.5rem' }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Preisverlauf je Geschäft" style={{ width: '100%', height: 'auto' }}>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--color-border)" strokeWidth={1} />
            <text x={PAD.left - 6} y={y(v) + 4} textAnchor="end" fontSize={11} fill="var(--color-muted)">{v.toFixed(2).replace('.', ',')}</text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text key={t} x={x(t)} y={H - 8} textAnchor="middle" fontSize={11} fill="var(--color-muted)">{fmtDate(t)}</text>
        ))}
        {stores.filter((s) => !hidden.has(s.key)).map((s) => {
          const pts = points.filter((p) => p.storeKey === s.key).sort((a, b) => a.date - b.date);
          return (
            <g key={s.key}>
              {pts.length > 1 && (
                <polyline fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" points={pts.map((p) => `${x(p.date)},${y(p.price)}`).join(' ')} />
              )}
              {pts.map((p, i) => (
                <circle
                  key={i}
                  cx={x(p.date)}
                  cy={y(p.price)}
                  r={hover === p ? 6 : 4}
                  fill={s.color}
                  stroke="var(--color-surface)"
                  strokeWidth={1.5}
                  onMouseEnter={() => setHover(p)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => setHover(p)}
                >
                  <title>{`${s.label}: ${fmtPrice(p.price)} am ${fmtDate(p.date)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
      <p className="muted" style={{ minHeight: '1.3em', margin: 0 }}>
        {hover ? `${hover.storeLabel}: ${fmtPrice(hover.price)} am ${fmtDate(hover.date)}` : 'Punkt antippen für Details.'}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem' }}>
        {stores.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => toggle(s.key)}
            aria-pressed={!hidden.has(s.key)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-text)', opacity: hidden.has(s.key) ? 0.4 : 1, font: 'inherit' }}
          >
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
